<article>
    <h1 i18n>🌙 Step 3: Rethinking courses in times of GenAI</h1>

    <p i18n>
        The previous steps aimed at checking the extent to which your current
        course design is resilient against the potential use of GenAI. In this
        third step, you are invited to reconsider exercises or assignments by
        involving generative AI in different ways. The AI assessment scale that
        this step is based on, has officially been accepted by the UU as a
        guiding principle. It helps teachers to think of generative AI in a less
        binary manner – not as something that is either forbidden or approved,
        but as a set of tools that can be integrated in academic education to
        various extents.
    </p>

    <p i18n>
        Below you will find a scale with five options of AI integration in
        educational assessments, based on the
        <a
            href="https://www.uu.nl/en/organisation/ai-policy/teachers/for-teachers/ai-index"
            target="_blank"
            i18n-href="url"
            >AI Index</a
        >
        for teachers, as adopted by Utrecht University. Additional information
        regarding the university policy on generative AI can be found
        <a
            href="https://www.uu.nl/en/organisation/ai-policy"
            i18n-href="url"
            target="_blank"
            >here</a
        >. (Both links will open in a new tab).
    </p>

    <figure
        class="d-flex flex-column align-items-center justify-content-center my-4"
        role="group"
    >
        <img
            tabindex="0"
            class="img-fluid rounded w-50 small-ai-scale-image"
            [ngSrc]="`../../assets/images/ai-index-${currentLocale}.svg`"
            alt="AI Index"
            (click)="openLightbox()"
            (keydown.enter)="openLightbox()"
            (keydown.space)="$event.preventDefault(); openLightbox()"
            role="button"
            width="691"
            height="782"
            priority
        />
        <figcaption class="fst-italic small mt-2" i18n>
            AI Index for teachers (click to enlarge)
        </figcaption>
    </figure>

    <p i18n>
        Click on one of the five options to find examples of educational
        exercises or assignments that make use of generative AI to various
        extents (options 2-5) or not at all (option 1). You can check off
        examples that you are interested in. These will be included in your
        final report.
    </p>
</article>

<section class="px-5 my-4">
    <gr-ai-scale-select [formControl]="selectedScaleLevel" />
</section>

@if (selectedScaleLevel.value) {
<section class="d-flex flex-column">
    <ng-template #popContent i18n>
        You are welcome to send your own examples for AI usages to
        <a href="mailto:genaitool@uu.nl">genaitool&#64;uu.nl</a>.
    </ng-template>
    <p>
        <span i18n>
            Select the options below that you would like to include in your
            summary report.
        </span>
        <a
            href="javascript:void(0)"
            i18n
            class="btn btn-sm btn-outline-secondary share-examples"
            [ngbPopover]="popContent"
        >
            Would you like to share your own examples?
        </a>
    </p>
    <div class="list-group example-item-group">
        @for (example of visibleExamples(); track example.id) {
        <li
            class="list-group-item d-flex align-items-center example-item"
            [class.selected]="chosenAiUses.value.includes(example.id)"
        >
            <input
                [id]="'example-' + example.id"
                type="checkbox"
                class="form-check-input"
                [value]="example.id"
                [checked]="chosenAiUses.value.includes(example.id)"
                (change)="onExampleChange(example.id)"
            />
            <label
                [for]="'example-' + example.id"
                class="form-check-label ms-2 w-100"
            >
                {{ example.text }}
            </label>
        </li>
        } @empty {
        <p i18n>No examples found.</p>
        }
    </div>
</section>
}

<gr-nav-buttons [navButtons]="navButtons" />

@if (showLightbox) {
<div class="modal fade show d-block" tabindex="-1" (click)="closeLightbox()">
    <div class="modal-dialog modal-dialog-centered modal-xl">
        <div class="modal-content border-0" (click)="$event.stopPropagation()">
            <div class="modal-header border-0" (click)="closeLightbox()">
                <button
                    #closeButton
                    type="button"
                    class="btn-close btn-close-white"
                    (click)="closeLightbox()"
                    aria-label="Close"
                    i18n-aria-label
                ></button>
            </div>
            <div class="modal-body text-center">
                <figure role="group">
                    <img
                        [src]="`../../assets/images/ai-index-${currentLocale}.svg`"
                        alt="AI Index"
                        class="img-fluid"
                    />
                    <figcaption
                        class="d-flex flex-column lightbox-caption mt-0 justify-content-center align-items-center"
                        i18n
                    >
                        <a
                            href="https://www.uu.nl/en/organisation/ai-policy/teachers/for-teachers/ai-index"
                            i18n-href="url"
                            target="_blank"
                        >
                            AI Index
                        </a>
                        <span class="ms-2 fst-italic"
                            >(Link will open in a new tab.)</span
                        >
                    </figcaption>
                </figure>
            </div>
        </div>
    </div>
</div>
}
